import { AfterViewInit, Component, ElementRef, NgModule, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { CustomButtonComponent } from '../../shared/custom-button/custom-button.component';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { ModalComponent } from '../../shared/modal/modal.component';
import { UserService } from '../../../services/user/user.service';
import { ModalFormComponent } from '../../shared/modal-form/modal-form.component';
import { empresasDoLogin } from '../../../utils/empresas-login.util';
import { ComputerDTO } from '../../../interfaces/computer-dto';
import { SmartQueueRegisterLog } from '../../../interfaces/smart-queue-register-log';
import { OperatorSessionService } from '../../../services/operator-session/operator-session.service';
import { BrandService } from '../../../services/brand/brand.service';
import { ItemSelecao, ListaSelecaoComponent } from '../../shared/lista-selecao/lista-selecao.component';
import { environment } from '../../../../environments/environment';


@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, CustomButtonComponent, ModalComponent, ModalFormComponent, ListaSelecaoComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements AfterViewInit {

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private operatorSessionService: OperatorSessionService,
    public brandService: BrandService,
  ) {
    this.userService.removeAuth();
  }

  loginForm!: FormGroup;

  isDarkMode = false;

  data: any;

  isLoading: boolean = false;
  openModal: boolean = false;
  selectUnitModal: boolean = false;
  enableTickedWindows: boolean = false;
  isSelectButtonDisabled: boolean = true;

  isAdminProfile = false;
  isProfileModalOpen: boolean = false;
  isProfileSelected = false;
  selectedProfile!: number;

  profiles: { id: number, name: string }[] = [{ id: 1, name: 'Operador' }];

  hasUnits: boolean = true;
  hasTicketWindow: boolean = true;

  unitName: string = '';
  selectedUnit: string = '';
  selectedWindow: string = '';
  errorMessage: string = 'Alguma coisa deu errado! Tente novamente';
  passwordFieldType: string = 'password';

  // Tela de login v2a: erro fica no campo (sem modal), Caps Lock e usuario lembrado.
  erroCredencial = '';
  capsLock = false;
  private static readonly CHAVE_ULTIMO_USUARIO = 'fila:ultimoUsuario';
  @ViewChild('campoUsuario') campoUsuario?: ElementRef<HTMLInputElement>;
  @ViewChild('campoSenha') campoSenha?: ElementRef<HTMLInputElement>;

  // O environment de desenvolvimento nao declara versao nem ambiente: caem nos padroes.
  readonly versao = (environment as { version?: string }).version ?? '';
  readonly ambiente = (environment as { ambiente?: string }).ambiente ?? (environment.production ? 'Produção' : 'Desenvolvimento');

  get suporte(): string {
    return this.brandService.suporteTelefone;
  }

  private static readonly CHAVE_ULTIMO_LOCAL = 'fila:ultimoLocal';
  private ultimoLocal: { unidade: string; guiche: string } | null = (() => {
    try { return JSON.parse(localStorage.getItem(LoginComponent.CHAVE_ULTIMO_LOCAL) ?? 'null'); } catch { return null; }
  })();
  get ultimaUnidade(): string | null { return this.ultimoLocal?.unidade ?? null; }
  get ultimoGuiche(): string | null {
    return this.ultimoLocal && this.ultimoLocal.unidade === this.selectedUnit ? this.ultimoLocal.guiche : null;
  }


  idTicketWindow!: number;
  units: { id: number, name: string }[] = [];
  ticketWindows: ComputerDTO[] = [];

  ngOnInit(): void {
    const darkMode = localStorage.getItem('darkMode');
    if (darkMode === 'enabled') {
      document.documentElement.classList.add('dark');
      this.isDarkMode = true;
    }

    let ultimoUsuario = '';
    try { ultimoUsuario = localStorage.getItem(LoginComponent.CHAVE_ULTIMO_USUARIO) ?? ''; } catch { /* sem storage */ }
    this.loginForm = new FormGroup({
      user: new FormControl(ultimoUsuario, [Validators.required]),
      password: new FormControl('', [Validators.required])
    })
    this.localStorageService.remove('selectedPassword');

    this.userService.removeAuth();
  }

  get user() {
    return this.loginForm.get('user')!;
  }

  get password() {
    return this.loginForm.get('password')!;
  }

  ngAfterViewInit(): void {
    // Com usuario lembrado o operador so digita a senha.
    setTimeout(() => (this.user.value ? this.campoSenha : this.campoUsuario)?.nativeElement.focus());
  }

  onTeclaSenha(event: KeyboardEvent): void {
    this.capsLock = !!event.getModifierState?.('CapsLock');
  }

  async submit() {
    // verificação de formulário inválido
    if (this.loginForm.invalid || this.isLoading) return;

    this.isLoading = true;
    this.erroCredencial = '';
    const { user, password } = this.loginForm.getRawValue();

    try {
      const response = await this.userService.login(user!, password!);

      if (response.data) {
        try { localStorage.setItem(LoginComponent.CHAVE_ULTIMO_USUARIO, String(user).trim()); } catch { /* sem storage */ }
        try {
          this.userService.setAuth(response.data[0]);
        } catch (err) {
          console.error(err);
          this.isLoading = false;
          this.openModal = true;
        } finally {
          await this.openUnitForm();
        }
      } else {
        // Resposta sem dados e sem erro: sem este ramo o botao ficava em "Entrando..." para sempre.
        this.erroCredencial = 'Usuário ou senha inválidos.';
        this.isLoading = false;
        this.password.reset('');
      }
    } catch (error) {
      console.error(error);
      this.erroCredencial = 'Usuário ou senha inválidos.';
      this.isLoading = false;
      // Mantem o usuario; so a senha e apagada.
      this.password.reset('');
      setTimeout(() => this.campoSenha?.nativeElement.focus());
    }
  }

  async openUnitForm() {
    this.isLoading = true;
    const user = this.localStorageService.get('login');
    await this.getUserByLogin(user);
    try {
      const empresas = empresasDoLogin(this.brandService.cdEmpresa);
      const respostas = (await Promise.all(empresas.map(e => this.userService.getUnits(user, e)))) as any[];
      let units: { id: number, name: string }[] = respostas.flatMap(r => r?.data || [])
        .sort((a, b) => a.name.localeCompare(b.name));

      const allowedIds = this.brandService.estabelecimentosIds;
      if (empresas.length === 1 && allowedIds.length > 0) {
        units = units.filter(u => allowedIds.includes(u.id));
      }

      this.units = units;
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
      this.units = [];
    } finally {
      this.hasUnits = this.units.length !== 0;
      this.selectUnitModal = true;
      this.isLoading = false;
    }
  }

  // Memorizadas pela lista de origem: recriar os itens a cada ciclo refaz o DOM da lista.
  private cacheUnidades: { de: unknown; itens: ItemSelecao[] } = { de: null, itens: [] };
  private cacheGuiches: { de: unknown; itens: ItemSelecao[] } = { de: null, itens: [] };

  get itensUnidades(): ItemSelecao[] {
    if (this.cacheUnidades.de !== this.units) {
      this.cacheUnidades = { de: this.units, itens: this.units.map(u => ({ valor: String(u.id), rotulo: u.name })) };
    }
    return this.cacheUnidades.itens;
  }

  get itensGuiches(): ItemSelecao[] {
    if (this.cacheGuiches.de !== this.ticketWindows) {
      this.cacheGuiches = { de: this.ticketWindows, itens: this.ticketWindows.map(t => ({ valor: t.name, rotulo: t.name })) };
    }
    return this.cacheGuiches.itens;
  }

  async selectUnit(valor: string) {
    this.ticketWindows = [];
    // Guiche escolhido era da unidade anterior.
    this.selectedWindow = '';
    this.selectedUnit = valor;
    const selectedUnitObj = this.units.find(unit => unit.id === Number(this.selectedUnit));
    if (selectedUnitObj) {
      this.unitName = selectedUnitObj.name;
    }
    this.selectedUnit = String(this.selectedUnit);
    this.localStorageService.set("currentUnit", this.selectedUnit);
    this.localStorageService.set("unitName", this.unitName);
    try {
      const response = await (await this.userService.getTicketsWindows(this.selectedUnit)).toPromise();
      this.ticketWindows = response.data;
    } catch (error) {
      console.error('Erro ao carregar guichês:', error);
      // Trate o erro conforme necessário
    } finally {
      this.hasTicketWindow = this.ticketWindows.length !== 0;
      if (this.hasTicketWindow) this.enableTickedWindows = true;
      this.checkSelectButtonState();
    }
  }

  selectTicketWindow(valor: string): void {
    this.selectedWindow = valor;

    const selectedWindowObj = this.ticketWindows.find(window => window.name === this.selectedWindow);
    if (selectedWindowObj) {
      this.idTicketWindow = selectedWindowObj.idTicketWindow;
    }
    const registerQueue = {
      1: 'all',
      2: 'private',
      3: 'plans'
    }[selectedWindowObj?.registeredQueue || 1];

    this.localStorageService.set('registerQueue', registerQueue);
    this.localStorageService.set('manualCall', selectedWindowObj?.manualCall);
    this.localStorageService.set('podePriorizar', selectedWindowObj?.podePriorizar ?? false);
    this.localStorageService.set('idTicketWindow', this.idTicketWindow);
    this.localStorageService.set("currentMachine", this.selectedWindow);
    this.userService.setTicketWindow(this.selectedWindow);
    this.checkSelectButtonState();
  }

  checkSelectButtonState(): void {
    this.isSelectButtonDisabled = !(this.selectedUnit && this.selectedWindow && this.selectedUnit !== 'Selecione a sua unidade' && this.selectedWindow !== 'Selecione o seu guichê');
  }

  closeModal(type: string) {
    if (type == 'profile') {
      this.isProfileModalOpen = false;
    } else {
      this.openModal = false;
    }
  }

  closeSelectUnitModal() {
    this.selectedUnit = '';
    this.selectedWindow = '';
    this.enableTickedWindows = false;
    this.isSelectButtonDisabled = true;
    this.selectUnitModal = false;
  }

  selectProfile(id: number) {
    this.selectedProfile = id;
    this.isProfileSelected = !!id;
  }

  get perfilEscolhido(): string {
    return this.profiles.find(p => p.id === this.selectedProfile)?.name ?? '';
  }

  // Uma linha do que cada perfil faz, para a escolha nao depender so do nome.
  descricaoPerfil(id: number): string {
    switch (id) {
      case 1: return 'Chama e atende as senhas no guichê';
      case 2: return 'Configura e acompanha a fila da unidade';
      case 3: return 'Fila e agenda da odontologia';
      case 4: return 'Acompanha a unidade e os operadores';
      case 5: return 'Chamada manual e senha Express';
      default: return '';
    }
  }

  setProfileSelect(profileId: number) {
    switch (profileId) {
      case 1:
        this.localStorageService.set('profile', 'Operador');
        break;
      case 2:
        this.localStorageService.set('profile', 'Administrador');
        break;
      case 3:
        this.localStorageService.set('profile', 'OdontoSiM');
        break;
      case 4:
        this.localStorageService.set('profile', 'Supervisor');
        break;
      case 5:
        this.localStorageService.set('profile', 'PosAtendimento');
        break;
    }

    this.isProfileModalOpen = false;

    this.goToHome();
  }


  async goToHome() {
    const profile = this.localStorageService.get('profile');
    if (profile !== 'Administrador' && profile !== 'Supervisor') {
      await this.userService.registerUserLog();

      const clinicId = Number(this.localStorageService.get('currentUnit'));
      const username = this.localStorageService.get('login');
      const machine = this.localStorageService.get('currentMachine') ?? this.selectedWindow;
      const ticketWindowId = Number(this.localStorageService.get('idTicketWindow'));
      if (clinicId && username && ticketWindowId) {
        await this.operatorSessionService.createSession(clinicId, username, machine, ticketWindowId);
      }
    }

    this.router.navigate(['/home']);
  }

  async getUserByLogin(login: string) {
    try {
      this.data = (await this.userService.getUserByLogin(login));

      const odontoProfile = 2080;

      if (this.data.profile.includes(2027)) {
        this.isAdminProfile = true;
        this.profiles.push({ id: 2, name: 'Administrador' });
      }
      if (this.data.profile.includes(2083)) {
        this.profiles.push({ id: 4, name: 'Supervisor' });
      }
      if (this.data.profile.includes(2098)) {
        this.profiles.push({ id: 5, name: 'Pós Atendimento' });
      }

      this.data.profile.forEach((profile: any) => {
        if (profile == odontoProfile) {
          this.profiles.push({ id: 3, name: 'OdontoSiM' });
        }
      });
    } catch (erro) {
      console.log(erro);
    } finally {
      this.localStorageService.set('profiles', this.profiles);
    }
  }

  goToDashboard() {
    // Grava so ao confirmar: escolher e voltar nao conta como uso.
    this.ultimoLocal = { unidade: this.selectedUnit, guiche: this.selectedWindow };
    try { localStorage.setItem(LoginComponent.CHAVE_ULTIMO_LOCAL, JSON.stringify(this.ultimoLocal)); } catch { /* sem storage */ }
    if (this.data.profile.length >= 1) {
      this.isProfileModalOpen = true;
    } else {
      this.localStorageService.set('profile', 'Operador');

      this.goToHome();
    }
  }

  togglePasswordVisibility(): void {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }
}


